import React from "react";
import "./Storyline.scss";
import Canvas from "../Canvas/Canvas";
import StorylineActions from "../actions";
import { useSelector } from "react-redux";
import { RootState, useThunkDispatch } from "../../store";
import { navigateTo as _navigateTo, goToID as _goToID } from "../../store/storyline/actions";
import { AppInsightsViewTracker, Loader, NavBar } from "../../shared/components";
import { useAuth0 } from "../../auth/AuthContext";
import { PERMISSIONS } from "../../auth/types";
import { ErrorContainer } from "../../shared/pages/ErrorContainer";
import { DevWindow } from "./DevWindow";

const storylineActions = <StorylineActions />;

interface StorylineProps {
};

const Storyline: React.FC<StorylineProps> = ({}) => {
    const storyline = useSelector((state: RootState) => state.storyline);
    const breadcrumbStack = useSelector((state: RootState) => state.app.breadcrumbStack);
    const pageError = useSelector((state: RootState) => state.app.errors.page);
    const { id, name, loading, canvases, currentParagraph, chapterIndex, pageIndex, paragraphIndex } = storyline;

    const { effectiveTags, userMetadata } = useAuth0();

    const [devWindowIsOpen, setDevWindowIsOpen] = React.useState(false);
    const toggleDevWindow = React.useCallback(() => setDevWindowIsOpen(x => !x), [setDevWindowIsOpen]);
    const setDevWindowIsClosed = React.useCallback(() => setDevWindowIsOpen(false), [setDevWindowIsOpen]);

    const dispatch = useThunkDispatch();
    const goToID = React.useCallback((...args: Parameters<typeof _goToID>) => dispatch(_goToID(...args)), []);
    const navigateTo = React.useCallback((...args: Parameters<typeof _navigateTo>) => dispatch(_navigateTo(...args)), []);
    

    React.useEffect(() => {
        const handleKeyUp = (e: KeyboardEvent) => {
            switch (e.key) {
                case "F2":
                case "F9":
                    if (effectiveTags?.includes?.(PERMISSIONS.DEVELOPER_TOOLS_ACCESS)) {
                        toggleDevWindow();
                    }
                    else {
                        console.log("Current user does not have access to the Canvas Debug window.");
                    }
                    break;
            }

            // We only want to handle global keyboard events here - ignore those from input fields, dropdowns, sliders, etc...
            const target = e.target as Element;
            if (target?.tagName?.toLowerCase() !== "body") return;

            switch (e.key) {
                case "ArrowUp":
                    if (e.ctrlKey) {
                        navigateTo("PREVIOUS_PAGE");
                    }
                    else {
                        navigateTo("PREVIOUS_PARAGRAPH");
                    }
                    break;
                case "ArrowDown":
                    if (e.ctrlKey) {
                        navigateTo("NEXT_PAGE");
                    }
                    else {
                        navigateTo("NEXT_PARAGRAPH");
                    }
                    break;
                case "ArrowLeft":
                    navigateTo("PREVIOUS_CHAPTER");
                    break;
                case "ArrowRight":
                    navigateTo("NEXT_CHAPTER");
                    break;
            }
        }

        document.addEventListener("keyup", handleKeyUp);
        return () => document.removeEventListener("keyup", handleKeyUp);
    }, [navigateTo]);

    return (
        <>
            <NavBar title={currentParagraph?.data?.["title"]} breadcrumbStack={breadcrumbStack} actions={storylineActions}>
            </NavBar>
            <ErrorContainer error={pageError}>
                {
                    loading ?
                        <div className="home-content"><Loader title="Loading Storyline..." description="Please wait while we get things ready." /></div> :

                        <div className="storyline">
                            <div className="main-canvas">
                                <AppInsightsViewTracker key={`storyline-tracker-${id}`} componentType="storyline" title={name} metadata={{ storylineName: name, userMetadata }}>
                                    <AppInsightsViewTracker key={`canvas-tracker-${chapterIndex}-${pageIndex}`} componentType="canvas" title={currentParagraph?.data?.["title"]} metadata={{ canvasName: canvases?.[chapterIndex]?.[pageIndex]?.[paragraphIndex]?.name, storylineName: name, userMetadata }}>
                                        <Canvas template={currentParagraph?.template} frameData={currentParagraph?.data} navigateTo={goToID} staticPlot={false} />
                                    </AppInsightsViewTracker>
                                </AppInsightsViewTracker>
                            </div>
                        </div>
                }
            </ErrorContainer>

            {
                devWindowIsOpen &&
                <DevWindow
                    setDevWindowIsClosed={setDevWindowIsClosed}
                    slide={canvases?.[chapterIndex]?.[pageIndex]?.[paragraphIndex]}
                    frameData={currentParagraph?.data}
                />
            }
        </>
    );
}

export default React.memo(Storyline);
